import type { ReactNode, HTMLAttributes } from 'react';
import styles from './Card.module.css';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  variant?: 'default' | 'elevated' | 'outlined';
  orientation?: 'vertical' | 'horizontal';
}

export function Card({ 
  children, 
  variant = 'default', 
  orientation = 'vertical',
  className, 
  ...props 
}: CardProps) {
  const classNames = [
    styles.card, 
    styles[variant], 
    orientation === 'horizontal' ? styles.horizontal : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
}

export interface CardStatsProps extends HTMLAttributes<HTMLDivElement> {
  align?: 'left' | 'center' | 'right';
  label?: ReactNode;
  value?: string | ReactNode;
  action?: ReactNode;
  topRightContent?: ReactNode;
  compactTopRight?: boolean;
  children?: ReactNode;
}

Card.Stats = function CardStats({
  align = 'left',
  label,
  value,
  action,
  topRightContent,
  compactTopRight,
  children,
  className,
  ...props
}: CardStatsProps) {
  const classNames = [
    styles.contentWrapper,
    align !== 'left' ? styles[`align-${align}`] : '',
    className
  ].filter(Boolean).join(' ');

  const renderValueSection = () => {
    if (action) {
      return (
        <div className={styles.valueRow}>
          {value && <div className={styles.value}>{value}</div>}
          <div className={styles.action}>{action}</div>
        </div>
      );
    }
    return value && <div className={styles.value}>{value}</div>;
  };

  return (
    <div className={classNames} {...props}>
      {topRightContent && (
        <div className={`${styles.topRight} ${compactTopRight ? styles.topRightCompact : ''}`}>
          {topRightContent}
        </div>
      )}
      {label && <div className={styles.label}>{label}</div>}
      {renderValueSection()}
      {children}
    </div>
  );
};

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

Card.Header = function CardHeader({ children, className, ...props }: CardHeaderProps) {
  const classNames = [styles.header, className].filter(Boolean).join(' ');
  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
};

interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

Card.Body = function CardBody({ children, className, ...props }: CardBodyProps) {
  const classNames = [styles.body, className].filter(Boolean).join(' ');
  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
};

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

Card.Footer = function CardFooter({ children, className, ...props }: CardFooterProps) {
  const classNames = [styles.footer, className].filter(Boolean).join(' ');
  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
};
